// Depth math for the bubble field. Each bubble samples a single value
// in its layer's depth range; that value drives size, opacity, blur,
// and per-bubble max speed via linear interpolation. Blur has a dead
// zone in the top BLUR_SHARP_FROM..1 range so the closest bubbles
// always render sharp.

import {
  BLUR_FAR_PX,
  BLUR_SHARP_FROM,
  BUBBLE_BASE_SIZE,
  DEPTH_LAYERS,
  MAX_SPEED,
  OPACITY_FAR,
  OPACITY_NEAR,
  SIZE_FAR_FACTOR,
  SIZE_NEAR_FACTOR,
  SPEED_FAR_FACTOR,
  SPEED_NEAR_FACTOR,
} from '@/utils/bubble-constants';

export type DepthDerived = {
  size: number;
  maxSpeed: number;
  opacity: number;
  blur: number;
};

export type BubbleSeed = { depth: number; layer: number };

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function blurFromDepth(depth: number): number {
  if (depth >= BLUR_SHARP_FROM) return 0;
  return BLUR_FAR_PX * (1 - depth / BLUR_SHARP_FROM);
}

export function deriveFromDepth(depth: number): DepthDerived {
  return {
    size: Math.round(
      BUBBLE_BASE_SIZE * lerp(SIZE_FAR_FACTOR, SIZE_NEAR_FACTOR, depth)
    ),
    maxSpeed: MAX_SPEED * lerp(SPEED_FAR_FACTOR, SPEED_NEAR_FACTOR, depth),
    opacity: lerp(OPACITY_FAR, OPACITY_NEAR, depth),
    blur: blurFromDepth(depth),
  };
}

function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = arr[i];
    const other = arr[j];
    if (tmp === undefined || other === undefined) continue;
    arr[i] = other;
    arr[j] = tmp;
  }
  return arr;
}

// Density scale clamped so phones don't run nearly empty and ultra-
// wides don't melt the GPU. Reference area = 1440×900 desktop.
const REF_AREA = 1440 * 900;
const SCALE_MIN = 0.25;
const SCALE_MAX = 1.5;

function viewportScale(width: number, height: number): number {
  const area = width * height;
  return Math.max(SCALE_MIN, Math.min(SCALE_MAX, area / REF_AREA));
}

export function buildBubbleSeeds(width: number, height: number): BubbleSeed[] {
  const scale = viewportScale(width, height);
  const seeds: BubbleSeed[] = [];
  for (let layer = 0; layer < DEPTH_LAYERS.length; layer++) {
    const cfg = DEPTH_LAYERS[layer];
    if (!cfg) continue;
    const count = Math.max(1, Math.round(cfg.count * scale));
    const span = cfg.depthMax - cfg.depthMin;
    for (let i = 0; i < count; i++) {
      seeds.push({ depth: cfg.depthMin + Math.random() * span, layer });
    }
  }
  return shuffle(seeds);
}
